import React, { useMemo } from "react";
import PropTypes from "prop-types";
import createSagaMiddleware from "redux-saga";
import { Provider } from "react-redux";
import { applyMiddleware, createStore, compose } from "redux";

import rootReducer from "../../Store/reducers/index";
import rootSaga from "../../Store/sagas/index";

let composeEnhancers = compose;
if (
  process.env.NODE_ENV === "development" &&
  window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
) {
  composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__;
}

const sagaMiddleware = createSagaMiddleware();

const loadState = () => {
  try {
    const serializedState = localStorage.getItem("state");
    if (serializedState === null) {
      return undefined;
    }
    const parsed = JSON.parse(serializedState);
    // Never hydrate heavy analytics caches from localStorage (causes UI freezes)
    if (parsed.dashboard) {
      delete parsed.dashboard;
    }
    if (parsed.salesAnalytics) {
      delete parsed.salesAnalytics;
    }
    return parsed;
  } catch (err) {
    return undefined;
  }
};

const saveState = (state) => {
  try {
    // Persist only lightweight slices — analytics cache stays in-memory Redux only
    const serializedState = JSON.stringify({
      playerprofile: state.playerprofile,
      transactionDetails: state.transactionDetails,
    });
    localStorage.setItem("state", serializedState);
  } catch (err) {
    console.error("Could not save state:", err);
  }
};

const configureStore = () => {
  const persistedState = loadState();
  const store = createStore(
    rootReducer,
    persistedState,
    composeEnhancers(applyMiddleware(sagaMiddleware))
  );

  let saveTimer = null;
  store.subscribe(() => {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      saveState(store.getState());
    }, 1000);
  });

  return store;
};

const Store = ({ children }) => {
  const store = useMemo(() => {
    const nextStore = configureStore();
    sagaMiddleware.run(rootSaga);
    return nextStore;
  }, []);

  return <Provider store={store}>{children}</Provider>;
};

Store.propTypes = {
  children: PropTypes.node.isRequired,
};

export default Store;
